/**
 * First-person arm + held item (vanilla ItemInHandRenderer).
 *
 *   const hand = new FirstPersonHand(itemRenderer.models);
 *   scene.add(player.camera);            // the camera must be in the scene for its children to render
 *   player.camera.add(hand.object3d);
 *   hand.update(dt, { heldItem, swing, usingItem, useProgress, sneaking, light, hidden, cooldown, yaw, pitch });
 *
 * Draw-on-top: the hand's meshes are transparent-pass objects with high renderOrder, and a first
 * dummy mesh clears the depth buffer right before them, so the hand never clips into walls while
 * still depth-sorting against itself. (Water/other transparents draw before it.)
 *
 * State: `swing` 0..1 (Player.swingProgress), `usingItem` 0 | 'bow' | 'eat', `useProgress`
 * (bow: charge ticks / 20, 1 = full; eat: fraction of the 32 ticks eaten), `cooldown` 0..1 attack
 * strength (the item dips after a swing, vanilla `f^3`), optional `yaw`/`pitch` (our convention) for
 * the arm sway lag. The empty hand draws the Steve right arm; with an item only the item is drawn.
 */

import * as THREE from 'three';
import { isEmpty, type Stack } from '../game/types';
import { syncGlint } from './glint';
import { I } from '../game/itemIds';
import { isSpearId } from '../game/spear';
import { tridentGeometry, TRIDENT_TEX } from './tridentModel';

const I_ENCHANTED_BOOK = I.ENCHANTED_BOOK;
import { createLitMaterial, HAND_LIGHT, setMeshLight, type ItemModelCache } from './itemEntities';

export interface HandState {
  heldItem: Stack | null;
  swing: number;
  usingItem: 0 | 'bow' | 'eat' | 'crossbow' | 'trident' | 'spear';
  /** Ticks a crossbow takes to load (25 - 5 * Quick Charge). */
  chargeTicks?: number;
  /** spear charge stage: 0 steady, 1 tired (sags and shakes), 2 dropped */
  spearStage?: 0 | 1 | 2;
  useProgress: number;
  sneaking: boolean;
  light: number;
  hidden: boolean;
  cooldown: number;
  yaw?: number;
  pitch?: number;
  /** Lightmap at the eye, 0..1: sky light already scaled by time of day, and block light. */
  sky?: number;
  block?: number;
  /** View bob shared with the camera (Player.bobPhase / bobAmount); 0 amount = off. */
  bobPhase?: number;
  bobAmount?: number;
  /** Off-hand item (not a shield): drawn mirrored on the left. */
  offhand?: Stack | null;
  /** A fishing bobber is out: the rod shows its `fishing_rod_cast` sprite. */
  rodCast?: boolean;
}

const DEG = Math.PI / 180;

/** Crossbow `firstperson_righthand`: vanilla's -90/0/-60 rotation (tiller forward, prod across), nudged up and forward to sit right in this hand frame. */
const CB_CHARGE_LIFT = 0.16;
const CB_CHARGE_PITCH = 0;
const CROSSBOW_FP = { rot: [-90, 0, -60], trans: [2, 2, -4], scale: 0.85 } as const;

/** Vanilla ModelPart cuboid in model space (px), vertex order and UV unwrap of `ModelPart.Cube`. */
export function boxGeometry(
  u: number, v: number, x: number, y: number, z: number, dx: number, dy: number, dz: number,
  grow: number, tw: number, th: number,
): THREE.BufferGeometry {
  const x0 = x - grow, y0 = y - grow, z0 = z - grow, x1 = x + dx + grow, y1 = y + dy + grow, z1 = z + dz + grow;
  const P = {
    p0: [x0, y0, z0], p1: [x1, y0, z0], p2: [x1, y1, z0], p3: [x0, y1, z0],
    p4: [x0, y0, z1], p5: [x1, y0, z1], p6: [x1, y1, z1], p7: [x0, y1, z1],
  } as const;
  const faces: Array<[Array<readonly number[]>, number, number, number, number, number[]]> = [
    [[P.p5, P.p4, P.p0, P.p1], u + dz, v, u + dz + dx, v + dz, [0, -1, 0]],
    [[P.p2, P.p3, P.p7, P.p6], u + dz + dx, v + dz, u + dz + dx + dx, v, [0, 1, 0]],
    [[P.p0, P.p4, P.p7, P.p3], u, v + dz, u + dz, v + dz + dy, [-1, 0, 0]],
    [[P.p1, P.p0, P.p3, P.p2], u + dz, v + dz, u + dz + dx, v + dz + dy, [0, 0, -1]],
    [[P.p5, P.p1, P.p2, P.p6], u + dz + dx, v + dz, u + dz + dx + dz, v + dz + dy, [1, 0, 0]],
    [[P.p4, P.p5, P.p6, P.p7], u + dz + dx + dz, v + dz, u + dz + dx + dz + dx, v + dz + dy, [0, 0, 1]],
  ];
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], tint: number[] = [], mask: number[] = [], idx: number[] = [];
  for (const [vs, u0, v0, u1, v1, n] of faces) {
    const base = pos.length / 3;
    // vertices map to (u1,v0),(u0,v0),(u0,v1),(u1,v1) like Polygon
    const tc = [[u1, v0], [u0, v0], [u0, v1], [u1, v1]];
    for (let i = 0; i < 4; i++) {
      pos.push(vs[i][0] / 16, vs[i][1] / 16, vs[i][2] / 16);
      nor.push(n[0], n[1], n[2]);
      uv.push(tc[i][0] / tw, 1 - tc[i][1] / th);
      tint.push(1, 1, 1); mask.push(0);
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('aTint', new THREE.Float32BufferAttribute(tint, 3));
  g.setAttribute('aMask', new THREE.Float32BufferAttribute(mask, 1));
  g.setIndex(idx);
  return g;
}

const mRotX = (d: number): THREE.Matrix4 => new THREE.Matrix4().makeRotationX(d * DEG);
const mRotY = (d: number): THREE.Matrix4 => new THREE.Matrix4().makeRotationY(d * DEG);
/** Roll (deg) that turns the diagonal in-hand spear sprite upright on screen. */
const SPEAR_UPRIGHT = -45;
/** Horizontal flip for the right-hand spear (the tuned pose is the left-hand one; three.js flips the winding for negative determinants). */
const SPEAR_MIRROR = new THREE.Matrix4().makeScale(-1, 1, 1);
const mRotZ = (d: number): THREE.Matrix4 => new THREE.Matrix4().makeRotationZ(d * DEG);
const mTrans = (x: number, y: number, z: number): THREE.Matrix4 => new THREE.Matrix4().makeTranslation(x, y, z);

const angDiff = (a: number, b: number): number => {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
};

export class FirstPersonHand {
  readonly object3d = new THREE.Group();

  private readonly holder = new THREE.Group();
  private readonly armGroup = new THREE.Group();
  private readonly itemMesh: THREE.Mesh;
  private readonly clearMesh: THREE.Mesh;
  private readonly offMesh: THREE.Mesh;
  private tridentMat: THREE.ShaderMaterial | null = null;
  private offHeight = 0;
  private offId = 0;
  private readonly armMat: THREE.ShaderMaterial;
  private readonly arm: THREE.Mesh;
  private readonly sleeve: THREE.Mesh;
  private skin: THREE.Texture | null = null;

  /** Equip animation: height 0..1 and the item being shown. */
  private height = 0;
  private prevHeight = 0;
  private shown: Stack | null = null;
  private shownId = 0;
  private tickAcc = 0;
  private bobYaw = 0;
  private bobPitch = 0;
  private haveBob = false;
  private useClock = 0;
  private lastUsing: HandState['usingItem'] = 0;
  private readonly m = new THREE.Matrix4();

  constructor(private readonly models: ItemModelCache) {
    const env = models.env;
    this.armMat = createLitMaterial(env, null, 'hand');
    // skin: 64x64 Steve; right arm at (40,16), sleeve overlay at (40,32)
    new THREE.TextureLoader().load('mc/entity/player/wide/steve.png', (t) => {
      t.colorSpace = THREE.SRGBColorSpace;
      t.magFilter = t.minFilter = THREE.NearestFilter;
      t.generateMipmaps = false;
      this.skin = t;
      this.armMat.uniforms.uMap.value = t;
    }, undefined, () => { /* no skin: arm stays untextured-black; hidden below */ });
    const arm = boxGeometry(40, 16, -3, -2, -2, 4, 12, 4, 0, 64, 64);
    const sleeve = boxGeometry(40, 32, -3, -2, -2, 4, 12, 4, 0.25, 64, 64);
    this.arm = new THREE.Mesh(arm, this.armMat);
    this.sleeve = new THREE.Mesh(sleeve, this.armMat);
    for (const [i, mesh] of [this.arm, this.sleeve].entries()) {
      mesh.frustumCulled = false;
      mesh.renderOrder = 1001 + i;
      // ModelPart pose of the wide right arm: offset (-5, 2, 0) px
      mesh.position.set(-5 / 16, 2 / 16, 0);
      this.armGroup.add(mesh);
    }
    this.itemMesh = new THREE.Mesh(new THREE.BufferGeometry(), this.armMat);
    this.itemMesh.frustumCulled = false;
    this.itemMesh.renderOrder = 1001;
    // depth clear: a degenerate transparent mesh drawn right before every hand part
    this.clearMesh = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, colorWrite: false }));
    this.clearMesh.geometry.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, 0, 0, 0, 0], 3));
    this.clearMesh.frustumCulled = false;
    this.clearMesh.renderOrder = 1000;
    // the depth mask must be on for the clear to do anything: a preceding
    // transparent pass (water, particles) leaves it off, and then the clear
    // silently does nothing and the held item clips into nearby blocks
    this.clearMesh.onBeforeRender = (r) => { r.state.buffers.depth.setMask(true); r.clearDepth(); };
    this.offMesh = new THREE.Mesh(new THREE.BufferGeometry(), this.armMat);
    this.offMesh.frustumCulled = false;
    this.offMesh.renderOrder = 1001;
    this.offMesh.matrixAutoUpdate = false;
    this.offMesh.visible = false;
    this.object3d.add(this.offMesh);
    for (const o of [this.armGroup, this.itemMesh]) { this.holder.add(o); }
    this.holder.matrixAutoUpdate = false;
    this.object3d.add(this.clearMesh, this.holder);
  }

  update(dt: number, s: HandState): void {
    // A shield is never drawn as a plain right-hand item: ShieldView owns it and
    // raises it on the left (vanilla keeps the shield on the off-hand).
    const held = s.heldItem && !isEmpty(s.heldItem) && s.heldItem.id !== I.SHIELD ? s.heldItem : null;
    this.object3d.visible = !s.hidden;
    if (s.hidden) return;

    // 20 Hz equip logic (ItemInHandRenderer.tick)
    this.tickAcc = Math.min(this.tickAcc + dt, 0.25);
    const first = this.shown === null && this.shownId === 0 && this.height === 0 && !this.haveBob;
    while (this.tickAcc >= 0.05 || first) {
      if (!first) this.tickAcc -= 0.05;
      this.prevHeight = this.height;
      const same = (held?.id ?? 0) === this.shownId;
      const c = Math.max(0, Math.min(1, s.cooldown));
      const target = same ? c * c * c : 0;
      this.height += Math.max(-0.4, Math.min(0.4, target - this.height));
      if (this.height < 0.1) { this.shown = held; this.shownId = held?.id ?? 0; }
      if (first) break;
    }
    const partial = Math.max(0, Math.min(1, this.tickAcc / 0.05));
    const equip = 1 - (this.prevHeight + (this.height - this.prevHeight) * partial);

    // arm sway: the view turns faster than the hand follows
    let swayX = 0, swayY = 0;
    if (s.yaw !== undefined && s.pitch !== undefined) {
      if (!this.haveBob) { this.bobYaw = s.yaw; this.bobPitch = s.pitch; this.haveBob = true; }
      swayX = -(s.pitch - this.bobPitch) / DEG * 0.1;
      swayY = -angDiff(s.yaw, this.bobYaw) / DEG * 0.1;
      const k = 1 - Math.pow(0.5, dt * 20);
      this.bobPitch += (s.pitch - this.bobPitch) * k;
      this.bobYaw += angDiff(s.yaw, this.bobYaw) * k;
    }

    // use clock (finer than the lead's per-tick progress)
    if (s.usingItem !== this.lastUsing) { this.useClock = 0; this.lastUsing = s.usingItem; }
    const total = s.usingItem === 'eat' ? 32 : s.usingItem === 'crossbow' ? (s.chargeTicks ?? 25) : 20;
    if (s.usingItem) {
      this.useClock += dt * 20;
      const auth = s.useProgress * total;
      if (Math.abs(this.useClock - auth) > 1.5) this.useClock = auth;
    }

    HAND_LIGHT.sky.value = Math.max(0, Math.min(1, s.sky ?? s.light));
    HAND_LIGHT.block.value = Math.max(0, Math.min(1, s.block ?? 0));

    const m = this.m.identity();
    // vanilla renders the hand with a fixed 70° FOV (GameRenderer.renderItemInHand uses
    // getFov(..., false)): squeeze view-space x/y so the hand keeps its size whatever
    // the FOV option or the sprint/fly FOV effect is. Exact for a perspective projection.
    const cam = this.object3d.parent as THREE.PerspectiveCamera | null;
    if (cam && (cam as THREE.PerspectiveCamera).isPerspectiveCamera) {
      const k = Math.tan((cam.fov * DEG) / 2) / Math.tan((70 * DEG) / 2);
      m.multiply(new THREE.Matrix4().makeScale(k, k, 1));
    }
    // vanilla GameRenderer.bobView applied to the hand pose, same phase/strength as the camera
    const bob = s.bobAmount ?? 0;
    if (bob > 1e-4) {
      const ph = s.bobPhase ?? 0;
      m.multiply(mTrans(Math.sin(ph) * bob * 0.5, -Math.abs(Math.cos(ph) * bob), 0));
      m.multiply(mRotZ(Math.sin(ph) * bob * 3));
      m.multiply(mRotX(Math.abs(Math.cos(ph - 0.2) * bob) * 5));
    }
    m.multiply(mRotX(swayX)).multiply(mRotY(swayY));
    const base = m.clone();
    const shown = this.shown;
    const swing = Math.max(0, Math.min(1, s.swing));
    const sq = Math.sqrt(swing);
    const sinPi = Math.sin(sq * Math.PI);

    this.armGroup.visible = false;
    this.itemMesh.visible = false;
    syncGlint(this.itemMesh, false);
    if (!shown) {
      // renderPlayerArm
      this.armGroup.visible = this.skin !== null;
      const f2 = -0.3 * sinPi, f3 = 0.4 * Math.sin(sq * Math.PI * 2), f4 = -0.4 * Math.sin(swing * Math.PI);
      m.multiply(mTrans(f2 + 0.64000005, f3 - 0.6 + equip * -0.6, f4 - 0.71999997));
      m.multiply(mRotY(45));
      m.multiply(mRotY(sinPi * 70));
      m.multiply(mRotZ(Math.sin(swing * swing * Math.PI) * -20));
      m.multiply(mTrans(-1, 3.6, 3.5));
      m.multiply(mRotZ(120)).multiply(mRotX(200)).multiply(mRotY(-135));
      m.multiply(mTrans(5.6, 0, 0));
      for (const o of [this.arm, this.sleeve]) setMeshLight(o, s.light);
    } else {
      const bowish = s.usingItem === 'bow';
      const eating = s.usingItem === 'eat';
      let spearRoll = 0; // in-plane roll of the sprite (about its own normal), applied after the display rotation
      const armTransform = (): void => { m.multiply(mTrans(0.56, -0.52 + equip * -0.6, -0.72)); };
      if (s.usingItem === 'trident') {
        // drawn back beside the head, prongs pointing forward; shakes once fully charged
        armTransform();
        const f11 = Math.min(1, this.useClock / 10);
        const shake = f11 >= 1 ? Math.sin(this.useClock * 2.1) * 0.006 : 0;
        m.multiply(mTrans(-0.35 + shake, 0.12, 0.3 + f11 * 0.3));
        m.multiply(mRotX(-70)).multiply(mRotY(12));
      } else if (s.usingItem === 'spear') {
        // spear charge: lowered and levelled forward under the arm, tip toward the crosshair (game/spear.ts)
        armTransform();
        const st = s.spearStage ?? 0;
        const shk = st === 1 ? Math.sin(this.useClock * 1.9) * 0.012 : 0;
        m.multiply(mTrans(-0.1 + shk, -0.1 - (st === 1 ? 0.06 : st === 2 ? 0.3 : 0), -0.2 + (st === 2 ? 0.1 : 0)));
        m.multiply(SPEAR_MIRROR); // right hand: mirror of the left-hand reference pose (head leans toward the centre)
        // upright hold (the sprite's diagonal made vertical), then pitched until the tip points down the view
        m.multiply(mRotZ(-45)).multiply(mRotX(-78 + (st === 1 ? 14 : st === 2 ? 50 : 0)));
      } else if (s.usingItem === 'crossbow') {
        // vanilla crossbow loading pose (right hand)
        armTransform();
        // vanilla's pull-in pose, lifted and levelled: the flat crossbow would otherwise be seen edge-on under the hotbar
        m.multiply(mTrans(-0.4785682, -0.094387 + CB_CHARGE_LIFT, 0.05731531));
        m.multiply(mRotX(CB_CHARGE_PITCH)).multiply(mRotY(65.3)).multiply(mRotZ(-9.785));
        const f9 = this.useClock;
        const f13 = Math.min(1, f9 / Math.max(1, s.chargeTicks ?? 25));
        if (f13 > 0.1) m.multiply(mTrans(0, Math.sin((f9 - 0.1) * 1.3) * (f13 - 0.1) * 0.004, 0));
        m.multiply(mTrans(0, 0, f13 * 0.04));
        m.multiply(new THREE.Matrix4().makeScale(1, 1, 1 + f13 * 0.2));
        m.multiply(mRotY(-45));
      } else if (bowish) {
        armTransform();
        m.multiply(mTrans(-0.2785682, 0.18344387, 0.15731531));
        m.multiply(mRotX(-13.935)).multiply(mRotY(35.3)).multiply(mRotZ(-9.785));
        const f8 = this.useClock;
        let f12 = f8 / 20;
        f12 = (f12 * f12 + f12 * 2) / 3;
        if (f12 > 1) f12 = 1;
        if (f12 > 0.1) {
          const f20 = Math.sin((f8 - 0.1) * 1.3) * (f12 - 0.1);
          m.multiply(mTrans(0, f20 * 0.004, 0));
        }
        m.multiply(mTrans(0, 0, f12 * 0.04));
        m.multiply(new THREE.Matrix4().makeScale(1, 1, 1 + f12 * 0.2));
        m.multiply(mRotY(-45));
      } else if (eating) {
        const f = total - this.useClock + 1;
        const f1 = f / total;
        if (f1 < 0.8) m.multiply(mTrans(0, Math.abs(Math.cos(f / 4 * Math.PI) * 0.1), 0));
        const f3 = 1 - Math.pow(Math.max(0, f1), 27);
        m.multiply(mTrans(f3 * 0.6, f3 * -0.5, 0));
        m.multiply(mRotY(f3 * 90)).multiply(mRotX(f3 * 10)).multiply(mRotZ(f3 * 30));
        armTransform();
      } else if (isSpearId(shown.id)) {
        // spear: carried low and forward; a jab thrusts it straight along the look direction
        const thrust = Math.sin(swing * Math.PI);
        armTransform();
        // vanilla melee_spear_hold: big sprite carried upright at the side (bedrock-samples player_firstperson.animation.json);
        // the jab pushes it straight ahead and tips the head toward the crosshair
        m.multiply(mTrans(-0.06, -0.02, -0.2 - 0.9 * thrust));
        m.multiply(SPEAR_MIRROR);
        spearRoll = SPEAR_UPRIGHT; m.multiply(mRotX(-8 - thrust * 40));
      } else {
        m.multiply(mTrans(-0.4 * sinPi, 0.2 * Math.sin(sq * Math.PI * 2), -0.2 * Math.sin(swing * Math.PI)));
        armTransform();
        m.multiply(mRotY(45 + Math.sin(swing * swing * Math.PI) * -20));
        m.multiply(mRotZ(sinPi * -20));
        m.multiply(mRotX(sinPi * -80));
        m.multiply(mRotY(-45));
      }
      // ItemInHandRenderer: a loaded crossbow is held out in front, a little toward the centre
      if (shown.id === I.CROSSBOW && shown.charged && s.usingItem !== 'crossbow' && swing < 0.001) {
        m.multiply(mTrans(-0.641864, 0, 0)).multiply(mRotY(10));
      }
      // vanilla draws the arm for the two-handed use poses, but it needs a
      // transform of its own: the item's pose stack has already been advanced
      // past the grip, and appending renderPlayerArm's tail here shoves the item
      // (which shares this matrix) off screen. The arm stays hidden for now.
      if (bowish || s.usingItem === 'crossbow' || s.usingItem === 'trident' || s.usingItem === 'spear' || isSpearId(shown.id)) {
        this.armGroup.visible = false;
      }
      // the item model with its first-person display transform
      const pullStage = bowish ? (s.useProgress >= 0.9 ? 2 : s.useProgress >= 0.65 ? 1 : 0) : -1;
      const model = pullStage >= 0 && shown.id === this.shownId && this.isBow(shown.id)
        ? this.models.getBow(shown.id, pullStage)
        : shown.id === I.CROSSBOW && (shown.charged || s.usingItem === 'crossbow')
          ? this.models.getSprite(shown.id, shown.charged ? (shown.fw ? 'crossbow_firework' : 'crossbow_arrow')
            : `crossbow_pulling_${s.useProgress >= 1 ? 2 : s.useProgress > 0.58 ? 1 : 0}`)
          : shown.id === I.FISHING_ROD && s.rodCast ? this.models.getSprite(shown.id, 'fishing_rod_cast')
          : this.models.getHand(shown.id);
      const trident = shown.id === I.TRIDENT;
      if (trident) {
        // the 3D model (grip at the origin, prongs +y) held upright, leaning away and slightly inward
        if (s.usingItem === 'trident') {
          m.multiply(new THREE.Matrix4().makeScale(0.7, 0.7, 0.7));
        } else {
          m.multiply(mTrans(0.04, 0.08, 0));
          m.multiply(mRotX(-10)).multiply(mRotZ(5));
          m.multiply(new THREE.Matrix4().makeScale(0.65, 0.65, 0.65));
        }
      } else {
      // Sprites: our extruded sprite faces +Z where vanilla's generated model's
      // front faces the other way, so the Y turn is mirrored to send the sprite's
      // +X (sword tip / pickaxe head) into the screen, up-left toward the crosshair.
      // the crossbow has its own display block (vanilla crossbow.json), not the shared bow / handheld one
      const cb = shown.id === I.CROSSBOW;
      const fpTrans = cb ? CROSSBOW_FP.trans : model.fpTrans, fpRot = cb ? CROSSBOW_FP.rot : model.fpRot, fpScale = cb ? CROSSBOW_FP.scale : model.fpScale;
      m.multiply(mTrans(fpTrans[0] / 16, fpTrans[1] / 16, fpTrans[2] / 16));
      m.multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(fpRot[0] * DEG, (model.block ? 1 : -1) * fpRot[1] * DEG, fpRot[2] * DEG, 'XYZ')));
      if (spearRoll) m.multiply(mRotZ(spearRoll));
      m.multiply(new THREE.Matrix4().makeScale(fpScale, fpScale, fpScale));
      }
      const im = this.itemMesh;
      im.geometry = trident ? tridentGeometry() : model.geometry;
      im.material = trident ? this.tridentMaterial() : model.material('hand');
      im.visible = true;
      syncGlint(im, !!shown.ench?.length || shown.id === I_ENCHANTED_BOOK);
      setMeshLight(im, s.light);
    }
    this.holder.matrix.copy(m);
    this.holder.matrixWorldNeedsUpdate = true;
    this.updateOffhand(dt, s, base);
  }

  private tridentMaterial(): THREE.ShaderMaterial {
    if (!this.tridentMat) {
      const tex = new THREE.TextureLoader().load(TRIDENT_TEX, (t) => { t.needsUpdate = true; });
      tex.magFilter = tex.minFilter = THREE.NearestFilter;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.generateMipmaps = false;
      this.tridentMat = createLitMaterial(this.models.env, tex, 'hand', true);
    }
    return this.tridentMat;
  }

  /** Off-hand item: vanilla's left-hand arm transform with the display mirrored (x, Y and Z rotation negate). */
  private updateOffhand(dt: number, s: HandState, base: THREE.Matrix4): void {
    const off = s.offhand && !isEmpty(s.offhand) && s.offhand.id !== I.SHIELD ? s.offhand : null;
    const id = off?.id ?? 0;
    // equip dip: lower the old item out, raise the new one in
    const target = id === this.offId ? 1 : 0;
    this.offHeight += Math.max(-0.4, Math.min(0.4, (target - this.offHeight) * Math.min(1, dt * 12)));
    if (this.offHeight < 0.05 && id !== this.offId) this.offId = id;
    this.offMesh.visible = false;
    if (!off || this.offId !== id) return;
    const model = this.models.get(id);
    const m = new THREE.Matrix4().copy(base);
    m.multiply(mTrans(-0.56, -0.52 + (1 - this.offHeight) * -0.6, -0.72));
    m.multiply(mTrans(-model.fpTrans[0] / 16, model.fpTrans[1] / 16, model.fpTrans[2] / 16));
    m.multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(model.fpRot[0] * DEG, (model.block ? -1 : 1) * model.fpRot[1] * DEG, -model.fpRot[2] * DEG, 'XYZ')));
    m.multiply(new THREE.Matrix4().makeScale(model.fpScale, model.fpScale, model.fpScale));
    this.offMesh.matrix.copy(m);
    this.offMesh.matrixWorldNeedsUpdate = true;
    this.offMesh.geometry = model.geometry;
    this.offMesh.material = model.material('hand');
    this.offMesh.visible = true;
    setMeshLight(this.offMesh, s.light);
  }

  private isBow(id: number): boolean { return id === this.bowId; }
  /** Item id of the bow (so the draw frames can be used). Set by the lead / dev page. */
  bowId = -1;

  dispose(): void {
    this.arm.geometry.dispose();
    this.sleeve.geometry.dispose();
    this.armMat.dispose();
    this.skin?.dispose();
  }
}
